/**
 * @zendir/ui - ImageFrame Component
 *
 * Renders a single-channel data frame (e.g. a CCD/sensor readout) as a
 * false-colour image on a <canvas>. Purely presentational: hand it an
 * array of samples and it paints them — it has no knowledge of where
 * the data came from (engine, file, websocket, console). This is the
 * reusable surface that callers "dump" a frame into.
 *
 * Pipeline per sample: normalise against a fixed ceiling (`maxADU`) to
 * an intensity in [0,1], run that through a colormap, pack RGBA. The
 * default `cividis` ramp (dark teal → yellow) reads well on dark UI.
 *
 * The canvas's native resolution is the frame's width×height, so each
 * sample is exactly one pixel; CSS scales it up with
 * `image-rendering: pixelated`, keeping small sensor frames crisp.
 *
 * Astro UX Compliance:
 * - Themed surface, border radius, and typography via `useTheme`.
 * - Loading and empty states with friendly messaging.
 * - Monospace raw-value grid for analysis.
 *
 * @example
 * ```tsx
 * <ImageFrame
 *   data={ccdSamples}      // number[] length = width*height
 *   width={28}
 *   maxADU={65535}
 *   title="CCD capture"
 *   showValues
 * />
 * ```
 */

import React, { memo, useEffect, useMemo, useRef } from "react";
import { useTheme } from "../../theme";
import { classNames, lerp } from "../../utils";

// =============================================================================
// Types
// =============================================================================

/** Built-in colormap names. */
export type ImageColormap = "cividis" | "gray";

export interface ImageFrameProps {
  /** Flat, row-major single-channel samples. Length must equal width*height. */
  data?: number[];
  /**
   * Pre-decoded RGBA pixels (length = width*height*4). When provided this
   * is painted directly and `data` / `maxADU` / `colormap` are ignored.
   * Escape hatch for callers that already have an image buffer.
   */
  pixels?: Uint8ClampedArray;
  /** Frame width in pixels. */
  width: number;
  /** Frame height in pixels. Defaults to `width` (square). */
  height?: number;
  /** Saturation ceiling used to normalise `data` → [0,1]. Default 65535. */
  maxADU?: number;
  /** Colormap applied to normalised intensities. Default 'cividis'. */
  colormap?: ImageColormap;
  /** Show the raw sample values as a grid below the image. Default false. */
  showValues?: boolean;
  /** Optional title shown above the image. */
  title?: string;
  /** Loading state (skeleton). */
  loading?: boolean;
  /** Message shown when there is no frame to display. */
  emptyMessage?: string;
  /** Custom class name. */
  className?: string;
}

// =============================================================================
// Pure helpers (exported for testing / reuse)
// =============================================================================

const DEFAULT_MAX_ADU = 65535;

/** Two-stop colormaps: intensity t in [0,1] → [r,g,b] (0..255). */
const COLORMAPS: Record<
  ImageColormap,
  (t: number) => [number, number, number]
> = {
  // Dark teal → yellow; a cividis-style ramp that reads well on dark UI.
  cividis: (t) => [lerp(42, 237, t), lerp(123, 222, t), lerp(155, 83, t)],
  gray: (t) => {
    const v = lerp(0, 255, t);
    return [v, v, v];
  },
};

/**
 * decodeImage — pure transform: single-channel samples → canvas-ready
 * RGBA. Fixed-ceiling normalisation (`sample / maxADU`) so the output
 * reflects the real signal level relative to saturation rather than
 * rescaling per frame. Returns null on invalid input.
 */
export function decodeImage(opts: {
  data: number[];
  width: number;
  height?: number;
  maxADU?: number;
  colormap?: ImageColormap;
}): { width: number; height: number; pixels: Uint8ClampedArray } | null {
  const {
    data,
    width,
    height,
    maxADU = DEFAULT_MAX_ADU,
    colormap = "cividis",
  } = opts;
  if (!Array.isArray(data)) return null;
  if (!Number.isFinite(width) || width <= 0) return null;
  const W = Math.floor(width);
  const H =
    Number.isFinite(height) && (height as number) > 0
      ? Math.floor(height as number)
      : W;
  if (data.length !== W * H) return null;

  const ramp = COLORMAPS[colormap] || COLORMAPS.cividis;
  const ceiling =
    Number.isFinite(maxADU) && maxADU > 0 ? maxADU : DEFAULT_MAX_ADU;

  const pixels = new Uint8ClampedArray(W * H * 4);
  for (let i = 0; i < W * H; i++) {
    const raw = data[i];
    // sample / ceiling (lerp clamps t to [0,1]); non-finite reads as 0.
    const t = Number.isFinite(raw) ? raw / ceiling : 0;
    const [r, g, b] = ramp(t);
    const off = i * 4;
    pixels[off + 0] = r;
    pixels[off + 1] = g;
    pixels[off + 2] = b;
    pixels[off + 3] = 255;
  }
  return { width: W, height: H, pixels };
}

/** Pretty-print samples as a width-padded grid for the values view. */
function formatValues(
  data: number[],
  width: number,
  height: number,
  maxADU: number,
): string {
  if (width * height !== data.length) return data.join(", ");
  const colW = Math.max(3, String(Math.floor(maxADU) || 1).length);
  const rows: string[] = [];
  for (let y = 0; y < height; y++) {
    const cells: string[] = [];
    for (let x = 0; x < width; x++) {
      cells.push(String(data[y * width + x]).padStart(colW, " "));
    }
    rows.push(cells.join(" "));
  }
  return rows.join("\n");
}

// =============================================================================
// Component
// =============================================================================

export const ImageFrame = memo(function ImageFrame({
  data,
  pixels,
  width,
  height,
  maxADU = DEFAULT_MAX_ADU,
  colormap = "cividis",
  showValues = false,
  title,
  loading = false,
  emptyMessage = "No frame to display.",
  className = "",
}: ImageFrameProps): React.ReactElement {
  const { tokens } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const H =
    Number.isFinite(height) && (height as number) > 0
      ? Math.floor(height as number)
      : Math.floor(width);
  const W = Math.floor(width);

  // Decode once per frame/option change. A pre-decoded `pixels` buffer
  // wins; otherwise decode `data` through the colormap.
  const decoded = useMemo(() => {
    if (pixels instanceof Uint8ClampedArray && pixels.length === W * H * 4) {
      return { width: W, height: H, pixels };
    }
    if (Array.isArray(data)) {
      return decodeImage({ data, width: W, height: H, maxADU, colormap });
    }
    return null;
  }, [data, pixels, W, H, maxADU, colormap]);

  useEffect(() => {
    if (!decoded) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = decoded.width;
    canvas.height = decoded.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Build via createImageData + set (rather than `new ImageData(buf,…)`)
    // to sidestep the lib.dom ArrayBuffer/ArrayBufferLike typing mismatch
    // on newer TypeScript, and it's the same cost.
    const img = ctx.createImageData(decoded.width, decoded.height);
    img.data.set(decoded.pixels);
    ctx.putImageData(img, 0, 0);
  }, [decoded]);

  const surfaceStyle: React.CSSProperties = {
    backgroundColor: tokens.colors.background.surface,
    borderRadius: tokens.borderRadius.lg,
    padding: tokens.spacing.md,
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamily.primary,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing.sm,
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-image-frame", "loading", className)}
        role="img"
        aria-busy="true"
        aria-label="Loading image"
        style={surfaceStyle}
      >
        {title && <FrameTitle tokens={tokens} title={title} />}
        <div
          style={{
            width: "100%",
            aspectRatio: `${W} / ${H}`,
            background: "rgba(255,255,255,0.06)",
            borderRadius: tokens.borderRadius.sm,
          }}
        />
      </div>
    );
  }

  // Empty state
  if (!decoded) {
    return (
      <div
        className={classNames("zendir-image-frame", "empty", className)}
        role="img"
        aria-label="No image"
        style={{
          ...surfaceStyle,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        {title && <FrameTitle tokens={tokens} title={title} />}
        <p style={{ margin: 0, fontSize: tokens.typography.fontSize.xs }}>
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div
      className={classNames("zendir-image-frame", className)}
      aria-label={title || "Image frame"}
      style={surfaceStyle}
    >
      {title && <FrameTitle tokens={tokens} title={title} />}

      <canvas
        ref={canvasRef}
        data-testid="image-frame-canvas"
        style={{
          width: "100%",
          aspectRatio: `${decoded.width} / ${decoded.height}`,
          imageRendering: "pixelated",
          display: "block",
          background: "rgba(0,0,0,0.4)",
          borderRadius: tokens.borderRadius.sm,
        }}
      />

      {showValues && Array.isArray(data) && (
        <pre
          data-testid="image-frame-values"
          style={{
            margin: 0,
            padding: tokens.spacing.sm,
            background: "rgba(0,0,0,0.45)",
            borderRadius: tokens.borderRadius.sm,
            color: tokens.colors.text.primary,
            fontSize: 10,
            lineHeight: 1.25,
            fontFamily: tokens.typography.fontFamily.mono,
            whiteSpace: "pre",
            overflow: "auto",
            maxHeight: 220,
          }}
        >
          {formatValues(data, decoded.width, decoded.height, maxADU)}
        </pre>
      )}
    </div>
  );
});

/** Small header row shared across states. */
const FrameTitle = ({
  tokens,
  title,
}: {
  tokens: ReturnType<typeof useTheme>["tokens"];
  title: string;
}) => (
  <h3
    style={{
      margin: 0,
      fontSize: tokens.typography.fontSize.md,
      fontWeight: tokens.typography.fontWeight.semibold,
      color: tokens.colors.text.primary,
    }}
  >
    {title}
  </h3>
);

export default ImageFrame;
